import { useEffect, useRef, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { isAxiosError } from "axios";
import type { AuthSessionResult } from "@shared/schemas/authentication.schemas";
import {
  OtpLinkErrorReason,
  OtpLinkErrorReasonSchema,
} from "@shared/schemas/authentication.schemas";
import { useAnonymousActions } from "@frontend/shared/features/actions/application/actions/use-anonymous-actions";
import { i18n } from "@frontend/i18n";
import { resolveRedirect } from "@frontend/shared/infrastructure/routing/resolve-user-route-redirect";

interface OtpAuthControllerReturn {
  readonly state: {
    readonly errorMessage: string | null;
    readonly isExpired: boolean;
    readonly isSuccess: boolean;
  };
}

/**
 * OTP auth view-model. Tracks errors via local state instead of the mutation
 * observer's isError flag because React 19 StrictMode destroys the first
 * observer in its mount→cleanup→mount cycle.
 */
export function useOtpAuthController(): OtpAuthControllerReturn {
  const [searchParams] = useSearchParams();
  const otp = searchParams.get("otp");
  const otpError = searchParams.get("otpError");

  const hasOtp = useMemo(() => otp !== null && otp !== "", [otp]);

  const createSessionMutation = useAnonymousActions().useCreateSession();

  // Track error locally — the mutation observer may be destroyed by StrictMode
  const [error, setError] = useState<Error | null>(null);

  // Prevent double-execution in React StrictMode
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // `otpError` means the backend already redeemed and rejected this code, so
    // redeeming again would contradict the error the page is rendering — and on
    // success would navigate away from it.
    if (
      hasStartedRef.current ||
      otpError !== null ||
      otp === null ||
      otp === ""
    ) {
      return;
    }

    hasStartedRef.current = true;

    // Use mutateAsync — its promise settles even if StrictMode destroys the
    // mutation observer.
    void createSessionMutation.mutateAsync(otp).then(
      (response: AuthSessionResult): void => {
        window.location.href = resolveRedirect(response.redirect);
      },
      (caughtError: unknown): void => {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError)),
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Fire once on mount when OTP is present
  }, [hasOtp]);

  const { errorMessage, isExpired } = deriveErrorState(otpError, hasOtp, error);

  return {
    state: {
      errorMessage,
      isExpired,
      isSuccess: createSessionMutation.isSuccess,
    },
  };
}

type OtpErrorState = { errorMessage: string | null; isExpired: boolean };

const expiredState = (): OtpErrorState => ({
  errorMessage: i18n.t("auth.otp.expired.message"),
  isExpired: true,
});

const genericState = (): OtpErrorState => ({
  errorMessage: i18n.t("auth.otp.error.generic"),
  isExpired: false,
});

function deriveErrorState(
  otpError: string | null,
  hasOtp: boolean,
  error: Error | null,
): OtpErrorState {
  // Checked before the missing-token branch: the backend's bounce carries
  // `otpError` and no `otp`, which would otherwise read as "no token supplied".
  if (otpError !== null) {
    // An unrecognized reason is still a bounced link, so it degrades rather than
    // falling through to the missing-token branch below.
    const parsed = OtpLinkErrorReasonSchema.safeParse(otpError);
    const reason: OtpLinkErrorReason = parsed.success
      ? parsed.data
      : OtpLinkErrorReason.GENERIC;

    switch (reason) {
      case OtpLinkErrorReason.EXPIRED:
        return expiredState();
      case OtpLinkErrorReason.GENERIC:
        return genericState();
    }
  }

  if (!hasOtp) {
    return { errorMessage: i18n.t("auth.otp.error.noToken"), isExpired: false };
  }

  if (error !== null) {
    return isAxiosError(error) && error.response?.status === 404
      ? expiredState()
      : genericState();
  }

  return { errorMessage: null, isExpired: false };
}
